import React, { useEffect, useState } from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
  Select,
  Typography,
  IconButton,
  Modal,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
  TextField,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import {
  EditOutlined,
  ContentCopyOutlined,
  DataObjectOutlined,
  TerminalOutlined,
  ChevronRight,
} from "@mui/icons-material";
import SmallScreenPreview from "./SmallScreenPreview";
import PresenceStack from "./presence/PresenceStack";
import { Icon as OpusIcon } from "./common/opus";
import { useActivity } from "../contexts/ActivityTrackingContext";
import apiService from "../services/api.service";
import { useAlert } from "../contexts/AlertContext";

// Derive the Aero env (dev/stg/prd) from the scene-renderer URL so the
// "Open in Scope" / "Open in Console" links point at the matching deploy
// without requiring extra env vars. Falls back to 'dev' and to explicit
// overrides (REACT_APP_SCOPE_URL / REACT_APP_CONSOLE_URL) when set.
const _sceneRendererBase = process.env.REACT_APP_SCENE_RENDERER_URL || "";
const _aeroEnvMatch = _sceneRendererBase.match(/-(dev|stg|prd)\./);
const AERO_ENV = _aeroEnvMatch ? _aeroEnvMatch[1] : "dev";
const SCOPE_BASE =
  process.env.REACT_APP_SCOPE_URL || `https://opus-scope-${AERO_ENV}.aelive.cloud`;
const CONSOLE_BASE =
  process.env.REACT_APP_CONSOLE_URL || `https://opus-console-${AERO_ENV}.aelive.cloud`;

const RendererConfig = ({
  edit,
  sx,
  rendererId,
  renderer, // ✅ Received as prop from parent
  sceneData, // ✅ Received as prop from parent
  sceneList,
  view,
  rendererTotal,
  createdBy,
  createdAt,
  viewers = [], // OPUS-913: live presence (who is viewing this renderer now)
  currentUserId, // OPUS-913: owner edit-lock ("lock to me")
  isSuperAdmin = false,
  onToggleOwnerLock,
}) => {
  const navigate = useNavigate();
  // ✅ Initialize with prop data
  const [rendererDetails, setRendererDetails] = useState(renderer);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [updatedRenderer, setUpdatedRenderer] = useState(null);
  const [loading, setLoading] = useState(false); // ✅ No initial loading
  const { trackActivity, ACTIVITY_TYPES, RESOURCE_TYPES } = useActivity();
  const [copySuccess, setCopySuccess] = useState(false);
  const alert = useAlert();

  const gridSize =
    rendererTotal === 1
      ? "100%"
      : rendererTotal < 5
        ? "50%"
        : rendererTotal < 10
          ? "33.33%"
          : "25%";

  // ✅ Update when prop changes
  useEffect(() => {
    if (renderer) {
      setRendererDetails(renderer);
    }
  }, [renderer]);

  const copyRendererLink = () => {
    const baseUrl =
      process.env.REACT_APP_SCENE_RENDERER_URL || "";
    const rendererLink = `${baseUrl}/?renderer=${rendererId}`;

    navigator.clipboard
      .writeText(rendererLink)
      .then(() => {
        setCopySuccess(true);
        trackActivity(
          ACTIVITY_TYPES.READ,
          RESOURCE_TYPES.RENDERER,
          rendererId,
          `Copied link for ${rendererDetails?.renderer_name}`,
        );
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  const handleCloseCopyAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setCopySuccess(false);
  };

  const openInScope = () => {
    if (!rendererId) return;
    const url = `${SCOPE_BASE}/?renderer=${rendererId}`;
    window.open(url, "_blank", "noopener,noreferrer");
    trackActivity(
      ACTIVITY_TYPES.READ,
      RESOURCE_TYPES.RENDERER,
      rendererId,
      `Opened in Scope: ${rendererDetails?.renderer_name || rendererId}`,
    );
  };

  const openInConsole = () => {
    if (!rendererId) return;
    const url = `${CONSOLE_BASE}/?renderer=${rendererId}`;
    window.open(url, "_blank", "noopener,noreferrer");
    trackActivity(
      ACTIVITY_TYPES.READ,
      RESOURCE_TYPES.RENDERER,
      rendererId,
      `Opened in Console: ${rendererDetails?.renderer_name || rendererId}`,
    );
  };


  const handleOpenEditModal = () => {
    if (!rendererDetails) return;

    const scene = sceneData || rendererDetails.sceneData;
    const techSpecs = rendererDetails.technical_specs || {};

    setUpdatedRenderer({
      renderer_name: rendererDetails.renderer_name,
      renderer_type: rendererDetails.renderer_type,
      renderer_status: rendererDetails.renderer_status,

      scene_id: scene?.id || rendererDetails.scene_id,

      // ✅ CLEAN TECHNICAL SPECS
      aspect_ratio: techSpecs.aspect_ratio || "16:9",
      resolution: techSpecs.resolution || "1920x1080",
      frame_rate: techSpecs.frame_rate || 60,
    });

    setEditModalOpen(true);
  };


  // const handleOpenEditModal = () => {
  //   if (!rendererDetails) return;

  //   // ✅ Use sceneData from props
  //   const scene = sceneData || rendererDetails.sceneData;

  //   // ✅ Extract allocation_details if it exists (from getRendererById response)
  //   const allocation = rendererDetails.allocation_details || {};
  //   const metadata = rendererDetails.metadata || {};
  //   const techSpecs = rendererDetails.technical_specs || {};

  //   setUpdatedRenderer({
  //     // Basic fields
  //     renderer_name: rendererDetails.renderer_name,
  //     renderer_type: rendererDetails.renderer_type,
  //     renderer_status: rendererDetails.renderer_status,
  //     connectivity_status: rendererDetails.connectivity_status,

  //     // Allocation details
  //     allocation_status: allocation.status || rendererDetails.allocation_status,
  //     allocation_scope: allocation.scope || rendererDetails.allocation_scope || "program",

  //     // Technical specs
  //     aspect_ratio: techSpecs.aspect_ratio || rendererDetails.aspect_ratio || "16:9",
  //     technical_specs: techSpecs,

  //     // Scene data (merged from props)
  //     scene_id: scene?.id || rendererDetails.scene_id,
  //     scene_path: scene?.scene_path || rendererDetails.scene_path,
  //     scene_name: scene?.name || rendererDetails.scene_name,
  //     scene_version: "1.0",

  //     // Owner (from allocation_details)
  //     owner_user_id: allocation.owner?.user_id || rendererDetails.owner_user_id,
  //     owner_organization_id: allocation.owner?.organization_id || rendererDetails.owner_organization_id,

  //     // Lease (from allocation_details)
  //     lease_start_time: allocation.lease?.start_time || rendererDetails.lease_start_time,
  //     lease_end_time: allocation.lease?.end_time || rendererDetails.lease_end_time,
  //     lease_duration_minutes: allocation.lease?.duration_minutes || rendererDetails.lease_duration_minutes,

  //     // Reason (from allocation_details)
  //     reason_type: allocation.reason?.type || rendererDetails.reason_type,
  //     reason_reference_id: allocation.reason?.reference_id || rendererDetails.reason_reference_id,
  //     reason_reference_name: allocation.reason?.reference_name || rendererDetails.reason_reference_name,
  //   });

  //   setEditModalOpen(true);
  // };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setUpdatedRenderer(null);
  };

  const handleUpdateRenderer = (field, value) => {
    setUpdatedRenderer(prev => ({
      ...prev,
      [field]: value,
    }));
  };


  const handleSubmitChanges = async () => {
    try {
      setLoading(true);

      const payload = {
        renderer_name: updatedRenderer.renderer_name,
        renderer_type: updatedRenderer.renderer_type,
        renderer_status: updatedRenderer.renderer_status,

        technical_specs: {
          aspect_ratio: updatedRenderer.aspect_ratio,
          resolution: updatedRenderer.resolution,
          frame_rate: Number(updatedRenderer.frame_rate),
        },
      };

      // ✅ Scene update (ONLY ID)
      if (updatedRenderer.scene_id) {
        payload.allocation_details = {
          scene: {
            id: updatedRenderer.scene_id,
          },
        };
      }

      await apiService.updateRenderer(rendererId, payload);

      trackActivity(
        ACTIVITY_TYPES.UPDATE,
        RESOURCE_TYPES.RENDERER,
        rendererId,
        updatedRenderer.renderer_name,
      );

      // ✅ Update local state cleanly
      setRendererDetails(prev => ({
        ...prev,
        renderer_name: updatedRenderer.renderer_name,
        renderer_type: updatedRenderer.renderer_type,
        renderer_status: updatedRenderer.renderer_status,
        scene_id: updatedRenderer.scene_id,
        technical_specs: payload.technical_specs,
      }));

      alert.success("Renderer updated successfully");
      handleCloseEditModal();

    } catch (error) {
      console.error("Error updating renderer:", error);
      alert.error("Failed to update renderer");

    } finally {
      setLoading(false);
    }
  };


  // const handleSubmitChanges = async () => {
  //   try {
  //     setLoading(true);

  //     const payload = {
  //       renderer_name: updatedRenderer?.renderer_name,
  //       renderer_type: updatedRenderer?.renderer_type,
  //       renderer_status: updatedRenderer?.renderer_status,
  //       connectivity_status: updatedRenderer?.connectivity_status,

  //       technical_specs: {
  //         aspect_ratio: updatedRenderer?.aspect_ratio,
  //         resolution: updatedRenderer?.technical_specs?.resolution || "1920x1080",
  //         frame_rate: updatedRenderer?.technical_specs?.frame_rate || 60,
  //       },

  //       allocation_details: {
  //         status: updatedRenderer?.allocation_status,
  //         scope: updatedRenderer?.allocation_scope,
  //       },
  //     };

  //     // ✅ Only include scene if scene_id exists
  //     if (updatedRenderer?.scene_id) {
  //       payload.allocation_details.scene = {
  //         id: updatedRenderer.scene_id,
  //         name: updatedRenderer.scene_name || "",
  //         path: updatedRenderer.scene_path || "",
  //         version: updatedRenderer.scene_version || "1.0",
  //       };
  //     }

  //     // ✅ Only include owner if both user_id and organization_id exist
  //     if (updatedRenderer?.owner_user_id && updatedRenderer?.owner_organization_id) {
  //       payload.allocation_details.owner = {
  //         user_id: updatedRenderer.owner_user_id,
  //         organization_id: updatedRenderer.owner_organization_id,
  //       };
  //     }

  //     // ✅ Only include lease if lease times exist
  //     if (updatedRenderer?.lease_start_time && updatedRenderer?.lease_end_time) {
  //       payload.allocation_details.lease = {
  //         start_time: updatedRenderer.lease_start_time,
  //         end_time: updatedRenderer.lease_end_time,
  //         duration_minutes: updatedRenderer.lease_duration_minutes,
  //       };
  //     }

  //     // ✅ Only include reason if reason data exists
  //     if (updatedRenderer?.reason_type && updatedRenderer?.reason_reference_id) {
  //       payload.allocation_details.reason = {
  //         type: updatedRenderer.reason_type,
  //         reference_id: updatedRenderer.reason_reference_id,
  //         reference_name: updatedRenderer.reason_reference_name,
  //       };
  //     }

  //     await apiService.updateRenderer(rendererId, payload);

  //     trackActivity(
  //       ACTIVITY_TYPES.UPDATE,
  //       RESOURCE_TYPES.RENDERER,
  //       rendererId,
  //       updatedRenderer?.renderer_name,
  //     );

  //     // ✅ Update local state with the changes
  //     setRendererDetails({
  //       ...rendererDetails,
  //       renderer_name: updatedRenderer.renderer_name,
  //       renderer_type: updatedRenderer.renderer_type,
  //       renderer_status: updatedRenderer.renderer_status,
  //       connectivity_status: updatedRenderer.connectivity_status,
  //       allocation_status: updatedRenderer.allocation_status,
  //       scene_id: updatedRenderer.scene_id,
  //       scene_name: updatedRenderer.scene_name,
  //       scene_path: updatedRenderer.scene_path,
  //       technical_specs: payload.technical_specs,
  //     });

  //     alert.success('Renderer updated successfully');
  //     handleCloseEditModal();

  //   } catch (error) {
  //     console.error("Error updating renderer:", error);
  //     alert.error(`Failed to update renderer: ${error.response?.data?.error?.message || error.message}`);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // OPUS-913: a renderer locked by another supervisor is closed to everyone
  // else — operators/other supervisors can see it in the list but can't open
  // it. The lock owner and super_admin are exempt (mirrors the lock badge
  // logic in the right-column icon below).
  const ownerLockState = rendererDetails?.owner_lock || {};
  const lockedByCurrentUser = ownerLockState.locked && String(ownerLockState.locked_by_user_id || "") === String(currentUserId || "");
  const isLockedOut = !!ownerLockState.locked && !lockedByCurrentUser && !isSuperAdmin;

  const handleRowClick = () => {
    if (isLockedOut || edit || view === "gridView" || !rendererDetails?.renderer_id) return;
    navigate(`/dashboard/scene-controls/${rendererDetails.renderer_id}`, { state: { renderer: rendererDetails } });
  };

  return (
    <>
      <Tooltip title={isLockedOut ? `Locked by ${ownerLockState.locked_by_name || "a supervisor"} — you can't open this renderer` : ""} disableHoverListener={!isLockedOut}>
      <Box
        onClick={handleRowClick}
        sx={{
          display: "flex",
          position: "relative",
          p: 0,
          m: view === "gridView" ? 0 : 0,
          pr: view === "gridView" ? 0 : "150px",
          float: view === "gridView" ? "left" : "",
          width: view === "gridView" ? gridSize : "100%",
          cursor: (!edit && view !== "gridView") ? (isLockedOut ? "not-allowed" : "pointer") : "default",
          "&:hover .renderer-name": isLockedOut ? {} : { textDecoration: "underline" },
          "&:hover": view !== "gridView" ? { backgroundColor: "#ffffff06" } : {},
          ...sx,
          ml: 0,
        }}
      >
        {view !== "gridView" && (
          // OPUS-913: presence avatar-stack + chevron, matching the design's right column.
          <Box sx={{ position: "absolute", right: 16, top: 0, bottom: 0, display: "flex", alignItems: "center", gap: 1.5, zIndex: 2 }}>
            {viewers && viewers.length > 0 && <PresenceStack viewers={viewers} size={26} label={false} />}
            {(() => {
              const ol = rendererDetails?.owner_lock || {};
              const createdById = rendererDetails?.created_by?.id ?? rendererDetails?.created_by;
              const isOwner = String(rendererDetails?.owner_user_id || createdById || "") === String(currentUserId || "");
              const lockedByMe = ol.locked && String(ol.locked_by_user_id || "") === String(currentUserId || "");
              if (ol.locked && (lockedByMe || isSuperAdmin)) {
                return (
                  <Tooltip title={`Locked by ${ol.locked_by_name || "owner"} — click to unlock`}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleOwnerLock && onToggleOwnerLock(rendererDetails, false); }} sx={{ color: "var(--amber)" }}>
                      <OpusIcon name="lock" size={16} />
                    </IconButton>
                  </Tooltip>
                );
              }
              if (ol.locked) {
                return (
                  <Tooltip title={`Locked by ${ol.locked_by_name || "owner"}`}>
                    <Box sx={{ display: "flex", color: "var(--amber)" }}><OpusIcon name="lock" size={16} /></Box>
                  </Tooltip>
                );
              }
              if (isOwner || isSuperAdmin) {
                return (
                  <Tooltip title="Lock to me (only you can edit while locked)">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleOwnerLock && onToggleOwnerLock(rendererDetails, true); }} sx={{ color: "var(--text-dim)", "&:hover": { color: "var(--amber)" } }}>
                      <OpusIcon name="unlock" size={16} />
                    </IconButton>
                  </Tooltip>
                );
              }
              return null;
            })()}
            <Box sx={{ display: "flex", alignItems: "center", color: "var(--text-dim)" }}>
              <OpusIcon name="chevronRight" size={18} />
            </Box>
          </Box>
        )}
        {view !== "gridView" && (
          loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "120px", flexGrow: 1 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            // OPUS-913: design RendererCard — single compact grid row (name+id |
            // 4 meta columns), divided by a bottom border; right cluster
            // (presence/lock/chevron) is the absolute box above.
            <Box
              sx={{
                flexGrow: 1,
                minWidth: 0,
                display: "grid",
                gridTemplateColumns:
                  view === "preview"
                    ? "200px minmax(200px,1.7fr) repeat(4, minmax(80px,1fr))"
                    : "minmax(240px,1.9fr) repeat(4, minmax(86px,1fr))",
                columnGap: "18px",
                alignItems: "start",
                padding: "18px 22px",
                borderBottom: "1px solid var(--border)",
              }}
            >
              {view === "preview" && (
                <Paper sx={{ p: 0, m: 0, width: "200px", backgroundColor: "#1e1e1e", color: "#fff", overflow: "hidden", borderRadius: "var(--r-md)" }}>
                  <SmallScreenPreview
                    name={"PROGRAM"}
                    url={process.env.REACT_APP_BROKER_URL}
                    rendererId={rendererId}
                    preview={false}
                    debug={false}
                    rendererLink={`/dashboard/scene-controls/${rendererDetails?.renderer_id}`}
                  />
                </Paper>
              )}

              {/* Col 1 — name + output id + actions */}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h2"
                  noWrap
                  sx={{ fontSize: "28px", fontWeight: 500, color: "#FF455C", letterSpacing: "-0.01em", lineHeight: 1.15, display: "inline-flex", alignItems: "center", maxWidth: "100%" }}
                >
                  {rendererDetails ? (
                    isLockedOut ? (
                      <Box
                        component="span"
                        className="renderer-name"
                        style={{ color: "#FF455C", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {rendererDetails?.renderer_name || "N/A"}
                      </Box>
                    ) : (
                      <Link
                        className="renderer-name"
                        to={`/dashboard/scene-controls/${rendererDetails.renderer_id}`}
                        state={{ renderer: rendererDetails }}
                        onClick={(e) => e.stopPropagation()}
                        style={{ textDecoration: "none", color: "#FF455C", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {rendererDetails?.renderer_name || "N/A"}
                      </Link>
                    )
                  ) : (
                    "Loading..."
                  )}
                  {edit && (
                    <Tooltip title="Edit">
                      <IconButton onClick={(e) => { e.stopPropagation(); handleOpenEditModal(e); }} sx={{ ml: 0.5, color: "var(--text-dim)", "&:hover": { color: "var(--green)" } }} size="small">
                        <OpusIcon name="edit" size={15} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: "10px", minWidth: 0 }}>
                  <Box component="span" sx={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {rendererDetails?.renderer_id || "—"}
                  </Box>
                  <Tooltip title="Copy renderer link">
                    <IconButton onClick={(e) => { e.stopPropagation(); copyRendererLink(e); }} size="small" sx={{ ml: 1, color: "var(--text-dim)", "&:hover": { color: "var(--green)" } }} disabled={!rendererDetails}>
                      <OpusIcon name="copy" size={15} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Open in Scope (data binding inspector)">
                    <IconButton onClick={(e) => { e.stopPropagation(); openInScope(e); }} size="small" sx={{ ml: 0.5, color: "var(--text-dim)", "&:hover": { color: "var(--green)" } }} disabled={!rendererId}>
                      <OpusIcon name="code" size={15} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Open in Console (broker command log)">
                    <IconButton onClick={(e) => { e.stopPropagation(); openInConsole(e); }} size="small" sx={{ ml: 0.5, color: "var(--text-dim)", "&:hover": { color: "var(--green)" } }} disabled={!rendererId}>
                      <OpusIcon name="terminal" size={15} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Meta columns — label above value (design MetaCol) */}
              {[
                { label: "Scene", value: sceneData?.name || rendererDetails?.scene_name || "—" },
                { label: "Environment", value: process.env.REACT_APP_ENV || "—" },
                { label: "Created At", value: createdAt ? new Date(createdAt).toLocaleString() : "—" },
                { label: "Created By", value: createdBy || "—" },
              ].map((m) => (
                <Box key={m.label} sx={{ minWidth: 0 }}>
                  <Box sx={{ fontSize: "15px", fontWeight: 400, color: "var(--text-muted)", mb: "16px", whiteSpace: "nowrap" }}>{m.label}</Box>
                  <Box sx={{ fontSize: "14px", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.value}</Box>
                </Box>
              ))}
            </Box>
          )
        )}

        {view === "gridView" && (
          <>
            <Paper
              sx={{
                p: 0,
                m: 0,
                width: "100%",
                backgroundColor: "#1e1e1e",
                color: "white",
              }}
            >
              <SmallScreenPreview
                name={"PROGRAM"}
                url={process.env.REACT_APP_BROKER_URL}
                rendererId={rendererId}
                preview={false}
                debug={false}
                rendererLink={`/dashboard/scene-controls/${rendererDetails?.renderer_id}`}
                view={view}
              />
            </Paper>
          </>
        )}
      </Box>
      </Tooltip>

      {/* Edit Modal */}
      <Modal open={editModalOpen} onClose={handleCloseEditModal}>
        <Paper
          sx={{
            p: 3,
            width: 600,
            margin: "auto",
            mt: 10,
            backgroundColor: "#1e1e1e",
            color: "#fff",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Edit Renderer Details
          </Typography>

          <TextField
            fullWidth
            label="Renderer Name"
            value={updatedRenderer?.renderer_name || ""}
            onChange={(e) =>
              handleUpdateRenderer("renderer_name", e.target.value)
            }
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Scene</InputLabel>
            <Select
              value={updatedRenderer?.scene_id || ""}
              onChange={(e) => handleUpdateRenderer("scene_id", e.target.value)}
            >
              {(Array.isArray(sceneList) ? sceneList : []).map((scene) => (
                <MenuItem key={scene.id} value={scene.id}>
                  {scene.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Renderer Type</InputLabel>
            <Select
              value={updatedRenderer?.renderer_type || ""}
              label="Renderer Type"
              onChange={(e) =>
                handleUpdateRenderer("renderer_type", e.target.value)
              }
            >
              <MenuItem value="Aero">Aero</MenuItem>
              <MenuItem value="OBS">OBS</MenuItem>
              <MenuItem value="Viz">Viz</MenuItem>
              <MenuItem value="Unreal">Unreal</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Renderer Status</InputLabel>
            <Select
              value={updatedRenderer?.renderer_status || ""}
              label="Renderer Status"
              onChange={(e) =>
                handleUpdateRenderer("renderer_status", e.target.value)
              }
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
            </Select>
          </FormControl>
          {/* 
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Connectivity</InputLabel>
            <Select
              value={updatedRenderer?.connectivity_status || ""}
              label="Connectivity"
              onChange={(e) =>
                handleUpdateRenderer("connectivity_status", e.target.value)
              }
            >
              <MenuItem value="online">Online</MenuItem>
              <MenuItem value="offline">Offline</MenuItem>
              <MenuItem value="connection_degraded">Degraded</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
            </Select>
          </FormControl> */}

          <TextField
            fullWidth
            label="Aspect Ratio"
            value={updatedRenderer?.aspect_ratio || ""}
            onChange={(e) =>
              handleUpdateRenderer("aspect_ratio", e.target.value)
            }
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Resolution"
            value={updatedRenderer?.resolution || ""}
            onChange={(e) =>
              handleUpdateRenderer("resolution", e.target.value)
            }
            sx={{ mb: 2 }}
          />



          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitChanges}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCloseEditModal}
            >
              Cancel
            </Button>
          </Box>
        </Paper>
      </Modal>

      {/* Copy success notification */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={handleCloseCopyAlert}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseCopyAlert}
          severity="success"
          sx={{ width: "100%" }}
        >
          Renderer link copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default RendererConfig;
